import {
  ArrowRight,
  BarChart3,
  HeartHandshake,
  Play,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import { MarketingButton } from "./ui/MarketingButton";
import { MarketingContainer } from "./ui/MarketingContainer";

const insightCards = [
  {
    icon: HeartHandshake,
    label: "Care follow-up",
    value: "12",
    detail: "members need attention",
  },
  {
    icon: Users,
    label: "Attendance",
    value: "87%",
    detail: "30-day engagement rate",
  },
  {
    icon: BarChart3,
    label: "Member health",
    value: "+14%",
    detail: "improved this month",
  },
];

export function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-white/5">
      <div className="absolute left-1/2 top-0 -z-10 h-[620px] w-[920px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[160px]" />
      <div className="absolute right-0 top-1/3 -z-10 h-80 w-80 rounded-full bg-cyan-500/10 blur-[120px]" />

      <MarketingContainer className="pb-24 pt-20 text-center sm:pt-28 lg:pb-32">
        <div className="mx-auto max-w-5xl">
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-indigo-400/20 bg-indigo-400/10 px-4 py-2 text-sm font-semibold text-indigo-200">
            <Sparkles className="h-4 w-4" />
            AI-powered church engagement platform
          </div>

          <h1 className="text-balance text-5xl font-black tracking-[-0.05em] text-white sm:text-6xl lg:text-8xl">
            <span className="block">
              See More.
            </span>
            <span className="block text-indigo-300">
              Care Better.
            </span>
            <span className="block">
              Lead Smarter.
            </span>
          </h1>

          <p className="mx-auto mt-8 max-w-3xl text-balance text-lg leading-8 text-slate-300 sm:text-xl">
            MinistryIQ helps churches identify
            disengagement early, strengthen
            follow-up, and turn attendance data
            into actionable ministry insight.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <MarketingButton
              href="/request-access"
              className="min-w-52"
            >
              Request Pilot Access
              <ArrowRight className="ml-2 h-4 w-4" />
            </MarketingButton>

            <MarketingButton
              href="#demo"
              variant="secondary"
              className="min-w-52"
            >
              <Play className="mr-2 h-4 w-4 fill-current" />
              Watch Demo
            </MarketingButton>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Invite-only pilot
            </span>

            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Guided onboarding
            </span>

            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Secure cloud platform
            </span>
          </div>
        </div>

        <div className="relative mx-auto mt-20 max-w-6xl">
          <div className="absolute inset-x-24 -top-10 -z-10 h-56 rounded-full bg-indigo-500/20 blur-3xl" />

          <div className="overflow-hidden rounded-3xl border border-white/10 bg-slate-900/90 p-3 shadow-2xl shadow-indigo-950/40 backdrop-blur">
            <div className="rounded-2xl border border-white/5 bg-slate-950 p-5 sm:p-7">
              <div className="mb-7 flex flex-col justify-between gap-4 border-b border-white/5 pb-6 text-left sm:flex-row sm:items-center">
                <div>
                  <p className="text-sm font-medium text-indigo-300">
                    Ministry Overview
                  </p>
                  <h2 className="mt-1 text-2xl font-bold text-white">
                    Good morning, Pastor
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Here is what needs your attention
                    today.
                  </p>
                </div>

                <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-left">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                    Church health
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">
                    Healthy
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {insightCards.map((card) => {
                  const Icon = card.icon;

                  return (
                    <div
                      key={card.label}
                      className="rounded-2xl border border-white/5 bg-white/[0.03] p-5 text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-400/10 text-indigo-300">
                          <Icon className="h-5 w-5" />
                        </div>

                        <span className="text-xs font-medium text-slate-500">
                          Last 30 days
                        </span>
                      </div>

                      <p className="mt-5 text-sm font-medium text-slate-400">
                        {card.label}
                      </p>

                      <p className="mt-1 text-3xl font-black text-white">
                        {card.value}
                      </p>

                      <p className="mt-2 text-sm text-slate-500">
                        {card.detail}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-[1.45fr_1fr]">
                <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5 text-left">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        Attendance trend
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Weekly engagement across your
                        church
                      </p>
                    </div>

                    <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                      +8.4%
                    </span>
                  </div>

                  <div className="mt-8 flex h-40 items-end gap-3">
                    {[46, 58, 52, 72, 64, 82, 88].map(
                      (height, index) => (
                        <div
                          key={`${height}-${index}`}
                          className="flex-1 rounded-t-lg bg-gradient-to-t from-indigo-600 to-indigo-300/80"
                          style={{
                            height: `${height}%`,
                          }}
                        />
                      ),
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5 text-left">
                  <p className="text-sm font-semibold text-white">
                    Priority center
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    People who may need care
                  </p>

                  <div className="mt-5 space-y-3">
                    {[
                      "3 members missed three services",
                      "2 households need follow-up",
                      "4 recent visitors are awaiting contact",
                    ].map((item) => (
                      <div
                        key={item}
                        className="flex gap-3 rounded-xl border border-white/5 bg-slate-950/60 p-3"
                      >
                        <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
                        <p className="text-sm leading-6 text-slate-300">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MarketingContainer>
    </section>
  );
}
